/**
 * Pagina di accesso al back-office.
 *
 * Il back-office e' l'unica parte del sistema dietro autenticazione: il portale
 * dei cittadini resta **liberamente accessibile, senza registrazione ne'
 * identificazione**, perche' condizionare l'accesso alla sezione AT violerebbe
 * la specifica ANAC. Qui invece si entra con credenziali e si esce con un JWT.
 *
 * Una scheda al centro della pagina, come l'accesso del back-office gemello:
 * il marchio, il modulo e una riga che ricorda che il portale non chiede
 * credenziali.
 */

import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ErroreApi } from "../api/client";
import { useAutenticazione } from "../auth/contesto";
import { CampoLibero } from "../components/campi";
import { Avviso, Bottone, InCaricamento, Scheda } from "../components/primitive";
import { SelettoreTema } from "../tema/FornitoreTema";
import logoAstra from "../assets/logo-astra.png";

export function Accesso() {
  const { utente, inAvvio, accedi } = useAutenticazione();
  const naviga = useNavigate();
  const posizione = useLocation();
  const destinazione = (posizione.state as { da?: string } | null)?.da ?? "/";

  const [nomeUtente, setNomeUtente] = useState("");
  const [password, setPassword] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    document.title = "Accesso · Amministrazione trasparente";
  }, []);

  if (inAvvio) return <InCaricamento etichetta="Ripristino della sessione…" />;
  if (utente) return <Navigate to={destinazione} replace />;

  const invia = async (evento: FormEvent) => {
    evento.preventDefault();
    setErrore(null);
    setInCorso(true);
    try {
      await accedi(nomeUtente.trim(), password);
      naviga(destinazione, { replace: true });
    } catch (causa) {
      setErrore(
        causa instanceof ErroreApi
          ? causa.messaggioEsteso
          : "Si e' verificato un errore imprevisto durante l'accesso.",
      );
    } finally {
      setInCorso(false);
    }
  };

  return (
    <main className="accesso">
      <div className="accesso__tema">
        <SelettoreTema />
      </div>

      <div className="accesso__scheda">
        <h1 className="visually-hidden">Accesso al back-office di Amministrazione trasparente</h1>
        <div className="accesso__marchio">
          <span className="barra__logo">
            <img src={logoAstra} alt="ASTRA — The Intelligence Layer" />
          </span>
          Amministrazione trasparente
        </div>

        <Scheda titolo="Accedi" descrizione="Inserisci le credenziali rilasciate dall'amministratore dell'ente.">
          <form className="accesso__modulo" onSubmit={invia}>
            {errore ? (
              <Avviso tono="critico" titolo="Accesso non riuscito">
                {errore}
              </Avviso>
            ) : null}

            <CampoLibero
              etichetta="Nome utente"
              valore={nomeUtente}
              onCambia={setNomeUtente}
              obbligatorio
              autoComplete="username"
              segnaposto="es. mario.rossi"
            />

            <CampoLibero
              etichetta="Password"
              tipo="password"
              valore={password}
              onCambia={setPassword}
              obbligatorio
              autoComplete="current-password"
            />

            <Bottone
              type="submit"
              variante="primario"
              dimensione="largo"
              inCorso={inCorso}
              disabled={!nomeUtente.trim() || !password}
            >
              Accedi
            </Bottone>

            <p className="campo__aiuto">
              Il portale pubblico non richiede alcuna autenticazione: questa pagina serve solo
              alla redazione dei contenuti.
            </p>
          </form>
        </Scheda>
      </div>
    </main>
  );
}
